import type { Draft, PayloadAction, SliceCaseReducers } from '@reduxjs/toolkit';

import { capitalize } from '~/shared/helpers/string-helpers';

type PaginationItem = Readonly<{
  _id: string;
}>;

type PaginationState<TItem> = {
  items: TItem[];
  isLoading: boolean;
  pageInfo: Record<string, unknown>;
};

type PaginationPayload<TItem> = Readonly<{
  items: TItem[];
  pageInfo: Record<string, unknown>;
}>;

export const getPaginationActions = <
  K extends string,
  TItem extends PaginationItem,
  TState extends Record<K, PaginationState<TItem>>,
>(
  query: K,
) => {
  const cap = capitalize(query);

  return {
    [`set${cap}Loading`]: (state: Draft<TState>, action: PayloadAction<boolean>) => {
      const typedState = state as Draft<Record<string, PaginationState<TItem>>>;
      typedState[query].isLoading = action.payload;
    },

    [`append${cap}`]: (state: Draft<TState>, action: PayloadAction<PaginationPayload<TItem>>) => {
      const typedState = state as Draft<Record<string, PaginationState<TItem>>>;
      const paginationState = typedState[query];

      const existing = paginationState.items;
      const incoming = action.payload.items;

      const seen = new Set(existing.map((item) => item._id));
      const filteredIncoming = incoming.filter((item) => !seen.has(item._id));

      paginationState.items = [...existing, ...(filteredIncoming as Draft<TItem>[])] as Draft<TItem>[];

      paginationState.pageInfo = action.payload.pageInfo;
      paginationState.isLoading = false;
    },
  } satisfies SliceCaseReducers<TState>;
};
